<template>
	<tr
		v-if="!shouldHideRow"
		class="meeting-row"
		:class="
			uploadState?.state === 'uploading'
				? 'text-gray-400 pointer-events-none'
				: meeting.status === 'errored'
					? 'text-red-500 pointer-events-none'
					: ''
		"
		@mouseout="mouseout"
		@mousemove="mousemove"
	>
		<td>
			<RouterLink
				tabindex="0"
				:to="{
					name: 'Meeting',
					params: { meetingId: meeting.displayId ?? meeting.id },
				}"
			>
				<span>{{
					meeting.status !== "errored"
						? getTitle(meeting)
						: "Opname kon niet geupload worden. Probeer het opnieuw"
				}}</span>
				<span
					v-if="recorder.meetingId === meeting.id && recorder.recording"
					class="recording-indicator flex items-center"
					title="Opname actief"
				>
					<span class="ht-icon">fiber_manual_record</span>
				</span>
				<AppProgress
					v-if="uploadState?.state === 'uploading'"
					:progress="(uploadState?.progress ?? 0) * 100"
					:stroke-width="4"
					:size="24"
					progress-color="var(--accent-primary)"
					background-color="var(--border)"
				/>
			</RouterLink>
		</td>
		<td>
			<RouterLink
				tabindex="-1"
				:to="{
					name: 'Meeting',
					params: { meetingId: meeting.displayId ?? meeting.id },
				}"
			>
				{{ formatDate(meeting.createdAt) }}
			</RouterLink>
		</td>
		<td>
			<RouterLink
				tabindex="-1"
				:to="{
					name: 'Meeting',
					params: { meetingId: meeting.displayId ?? meeting.id },
				}"
			>
				{{ formatDuration(meeting.durationInSeconds) }}
			</RouterLink>
		</td>
	</tr>
</template>

<script setup lang="ts">
import { getTitle } from "@/helpers/conversationHelpers";
import { useGlobalUploadStore } from "@/store/useGlobalUploadStore";
import { useRecorderStore } from "@/store/useRecorderStore";
import type { Schema } from "@loanlink-nl/zero";
import type { Row } from "@rocicorp/zero";
import { DateTime } from "luxon";
import { computed } from "vue";

const props = defineProps<{
	meeting: Omit<
		Row<Schema["tables"]["conversationTable"]>,
		"transcriptionResult" | "summary"
	>;
}>();

const globalUploadStore = useGlobalUploadStore();
const recorder = useRecorderStore();

const uploadState = computed(() =>
	globalUploadStore.getUploadState(props.meeting.recordingId),
);
const shouldHideRow = computed(
	() => props.meeting.status === "errored" && !uploadState.value,
);

function mousemove(e: Event) {
	if (e.target instanceof HTMLElement) {
		e.target.closest(".meeting-row")?.querySelector("a")?.focus();
	}
}

function mouseout(e: Event) {
	if (e.target instanceof HTMLElement) {
		e.target.closest(".meeting-row")?.querySelector("a")?.blur();
	}
}

function formatDate(date: number | null) {
	if (!date) {
		return;
	}

	return new Intl.DateTimeFormat("nl-NL", {
		day: "numeric",
		month: "long",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(DateTime.fromMillis(date).toJSDate());
}

function formatDuration(seconds?: number | null) {
	if (!seconds) return "-";

	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;

	return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}
</script>
